import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  fetchAlertsForDevices,
  fetchDeviceDetails,
  selectDevices,
  setDeviceDetailsScreen,
} from '../../reducers/devices'
import { useAppDispatch } from '../../reducers/store'
import { DeviceOverviewInformation } from '../../types/DeviceTypes'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../strings/api-consts'
import { useDispatch, useSelector } from 'react-redux'
import DeviceOverviewScans from './DeviceOverviewScans'
import { ActivityPopoutRequest } from '../../types/ActivityTypes'
import Popout from '../popout/Popout'
import AlertsResolveModal from '../alert-console/AlertsResolveModal'
import { closeAlerts, simulateAlert } from '../../reducers/alerts'
import toast from 'react-hot-toast'
import { AlertSimType, AlertType, ResolveAlert } from '../../types/AlertTypes'
import { selectUser } from '../../reducers/users'
import { inPov } from '../../utils/session'
import { HEADER_HEIGHT_WITH_TRIAL, HEADER_HEIGHT_WITHOUT_TRIAL } from '../../strings/consts'
import AlertSimulateModal from '../alert-console/AlertSimulateModal'
import { fetchConditionalAccessStatus, fetchMTDStatus } from '../../reducers/settings'
import { BOTTOM_TOAST_CONFIG } from '../toast/consts'
import DeviceOverviewEvents from './DeviceOverviewEvents'
import DeviceOverviewSummary from './DeviceOverviewSummary'
import { DeviceEvent } from '../../types/DeviceEventTypes'
import DeviceOverviewForensics from './DeviceOverviewForensics'
import { DeviceForensic } from '../../types/DeviceForensicTypes'

const DeviceOverview: React.FC = () => {
  const di = useAppDispatch()
  const { deviceId } = useParams()
  const dispatch = useDispatch()
  const { session } = useSelector(selectUser)
  const { deviceDetailsScreen } = useSelector(selectDevices)
  const [fetchedDetails, setFetchedDetails] = useState<DeviceOverviewInformation | undefined>(undefined)
  const [popoutScanData, setPopoutScanData] = useState<ActivityPopoutRequest | undefined>(undefined)
  const [popoutAlertData, setPopoutAlertData] = useState<
    { alertId: number; scanId: string; alertType: AlertType } | undefined
  >(undefined)
  const [popoutEventData, setPopoutEventData] = useState<DeviceEvent | undefined>(undefined)
  const [popoutForensicData, setPopoutForensicData] = useState<DeviceForensic | undefined>(undefined)
  const [showScanPopout, setShowScanPopout] = useState<boolean>(false)
  const [showAlertPopout, setShowAlertPopout] = useState<boolean>(false)
  const [showEventPopout, setShowEventPopout] = useState<boolean>(false)
  const [showForensicPopout, setShowForensicPopout] = useState<boolean>(false)
  const [showResolveModal, setShowResolveModal] = useState<boolean>(false)
  const [showSimulateAlertModal, setShowSimulateAlertModal] = useState<boolean>(false)
  const [caEnabled, setCaEnabled] = useState<boolean>(false)

  const defaultSortOrder = 'desc'
  const defaultSortColumn = 'lastSeen'
  const [sortAlertOrder, setAlertSortOrder] = useState<string>(defaultSortOrder)
  const [sortColumn, setSortColumn] = useState<string>(defaultSortColumn)

  useEffect(() => {
    if (deviceId) {
      fetchDeviceDetailsWithDeviceId(deviceId!)
      fetchConditionalAccess()
    }
  }, [deviceId])

  const fetchDeviceDetailsWithDeviceId = async (deviceId: string) => {
    const fetchedDetails = await di(fetchDeviceDetails(deviceId))
    if (!fetchedDetails) {
      return
    }
    setFetchedDetails(fetchedDetails)
  }

  const fetchConditionalAccess = async () => {
    let isActive = false
    const caSet = await di(fetchConditionalAccessStatus())
    const mtdSet = await di(fetchMTDStatus())
    if (caSet || mtdSet) {
      isActive = true
    }
    setCaEnabled(isActive)
  }

  const handleSimulateAlert = (alert: AlertSimType) => {
    if (!deviceId) {
      return
    }
    di(simulateAlert(alert, deviceId)).then(async (res) => {
      if (res === true) {
        toast.success(`Simulated alert successfully`, BOTTOM_TOAST_CONFIG)
        setShowSimulateAlertModal(false)
        await di(fetchAlertsForDevices(deviceId!, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, sortAlertOrder, sortColumn))
      } else {
        toast.error('There was an issue simulating the alert', BOTTOM_TOAST_CONFIG)
      }
    })
  }

  const handleCloseAlert = async (alerts: ResolveAlert[]) => {
    const closed = await di(closeAlerts(alerts))
    if (closed) {
      toast.success('Alert has been successfully resolved', BOTTOM_TOAST_CONFIG)
      await di(fetchAlertsForDevices(deviceId!, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, sortAlertOrder, sortColumn))
      fetchDeviceDetailsWithDeviceId(deviceId!)
      setShowAlertPopout(false)
    } else {
      toast.error('There was an issue resolving the alert', BOTTOM_TOAST_CONFIG)
    }
  }

  useEffect(() => {
    return () => {
      dispatch(setDeviceDetailsScreen('overview'))
    }
  }, [])

  useEffect(() => {
    setShowAlertPopout(false)
    setShowEventPopout(false)
    setShowForensicPopout(false)
    setShowScanPopout(false)
  }, [deviceDetailsScreen])

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] p-9"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        <div className="bg-contentBackground h-full max-w-[1600px] mx-auto overflow-y-auto">
          {deviceDetailsScreen === 'overview' && (
            <DeviceOverviewSummary
              fetchedDetails={fetchedDetails}
              setPopoutAlertData={setPopoutAlertData}
              setShowAlertPopout={setShowAlertPopout}
              setShowSimulateAlertModal={setShowSimulateAlertModal}
              setAlertSortOrder={setAlertSortOrder}
              setAlertSortColumn={setSortColumn}
              sortAlertOrder={sortAlertOrder}
              sortAlertColumn={sortColumn}
            />
          )}
          {deviceDetailsScreen === 'scans' && (
            <>
              <DeviceOverviewScans
                setPopoutData={setPopoutScanData}
                setShowPopout={setShowScanPopout}
                demoDevice={fetchedDetails?.demoDevice ?? false}
              />
            </>
          )}
          {deviceDetailsScreen === 'events' && (
            <>
              <DeviceOverviewEvents setPopoutData={setPopoutEventData} setShowPopout={setShowEventPopout} />
            </>
          )}
          {deviceDetailsScreen === 'forensics' && (
            <>
              <DeviceOverviewForensics setPopoutData={setPopoutForensicData} setShowPopout={setShowForensicPopout} />
            </>
          )}
        </div>
      </div>
      {showScanPopout && (
        <Popout
          setShowPopout={setShowScanPopout}
          contentScreen={popoutScanData!.scanType === 'EDR' ? 'EDR' : 'DEEP'}
          popoutData={popoutScanData}
        />
      )}
      {showAlertPopout && (
        <Popout
          setShowPopout={setShowAlertPopout}
          contentScreen={'ALERT'}
          popoutData={popoutAlertData?.alertId}
          setShowResolveModal={setShowResolveModal}
        />
      )}
      {showEventPopout && (
        <Popout setShowPopout={setShowEventPopout} contentScreen={'DEVICE_EVENT'} popoutData={popoutEventData} />
      )}

      {showForensicPopout && (
        <Popout
          setShowPopout={setShowForensicPopout}
          contentScreen={'DEVICE_FORENSIC'}
          popoutData={popoutForensicData}
        />
      )}
      {showResolveModal && (
        <AlertsResolveModal
          setShowResolveModal={setShowResolveModal}
          resolveAlerts={handleCloseAlert}
          alerts={[
            {
              alertId: popoutAlertData!.alertId,
              scanId: popoutAlertData!.scanId,
              alertType: popoutAlertData!.alertType,
            },
          ]}
        />
      )}
      {showSimulateAlertModal && (
        <AlertSimulateModal
          setShowSimulateAlertModal={setShowSimulateAlertModal}
          platform={fetchedDetails?.platform ?? 'IOS'}
          simulateAlert={handleSimulateAlert}
          caEnabled={caEnabled}
        />
      )}
    </>
  )
}

export default DeviceOverview
